import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Search, RefreshCw } from "lucide-react";

/**
 * Admin panel — every student that ticked the age & terms declaration at signup,
 * newest first. Backed by GET /api/admin/legal-acceptances. CSV export via
 * /api/admin/legal-acceptances.csv (uses the same admin JWT via the ?auth=…
 * fallback so the browser can open it in a new tab).
 */
function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminLegalAcceptancesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/legal-acceptances?limit=1000")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q1 = (q || "").toLowerCase().trim();
    if (!q1) return rows;
    return rows.filter((r) =>
      [r.name, r.email, r.phone, r.ip, r.declaration_version]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q1))
    );
  }, [q, rows]);

  const openCsv = () => {
    const token = localStorage.getItem("hn_token") || "";
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/legal-acceptances.csv?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="admin-legal-acceptances-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <FileText className="text-orange-800" size={18} />
          <div className="font-display font-bold text-lg">Student declarations</div>
          <Badge variant="outline" className="text-[10px]">{filtered.length}{q ? ` / ${rows.length}` : ""}</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by name, email, phone…"
              className="pl-8 h-9 rounded-full"
              data-testid="legal-acceptances-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-full gap-1" data-testid="legal-acceptances-refresh">
            <RefreshCw size={12} /> Refresh
          </Button>
          <Button
            onClick={openCsv}
            className="rounded-full bg-orange-800 hover:bg-orange-900 text-white gap-1"
            data-testid="legal-acceptances-csv-btn"
          >
            <Download size={14} /> Export CSV
          </Button>
        </div>
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Every student ticks the age &amp; terms declaration at signup. Each row here is the immutable audit record — name, email, phone, IP, timestamp, and the exact declaration version they accepted.
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-slate-500 py-8 text-center border border-dashed border-slate-200 rounded-xl">
          {rows.length === 0 ? "No student declarations recorded yet." : "No declarations match your filter."}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-slate-500 uppercase tracking-wider">
              <tr>
                <th className="py-2 px-3">When</th>
                <th className="py-2 px-3">Student</th>
                <th className="py-2 px-3">Contact</th>
                <th className="py-2 px-3">IP</th>
                <th className="py-2 px-3">Version</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`legal-acceptance-${r.id}`} className="border-t border-slate-100 align-top">
                  <td className="py-2 px-3 text-slate-700 whitespace-nowrap">{fmt(r.at)}</td>
                  <td className="py-2 px-3">
                    <div className="font-semibold text-slate-800 break-all">{r.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{r.user_id}</div>
                  </td>
                  <td className="py-2 px-3">
                    <div className="text-slate-700 break-all">{r.email}</div>
                    <div className="text-xs text-slate-500">{r.phone || "—"}</div>
                  </td>
                  <td className="py-2 px-3 text-slate-500 text-xs font-mono">{r.ip || "—"}</td>
                  <td className="py-2 px-3">
                    <Badge variant="outline" className="text-[10px] font-mono">{r.declaration_version || "—"}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
